/*
 * Gerçek zamanlı kanal istemcisi (backend services/realtime.py, `/api/ws`).
 *
 * Sayfa başına tek bağlantı. Jeton URL'de gitmez: bağlantı açılınca ilk mesaj
 * `{op:'auth', token}`'dır. Sunucu `hello` ile tam durumu verir; sonra yalnız
 * değişen şey gelir (`wallet`, `account`, `messages`, `settings`,
 * `admin.finance`, `admin.player`).
 *
 * Kurallar:
 *  - Kanal yalnız **gösterimdir**: bakiye mutlak değerdir, artış değil. Sıra
 *    numarası (`s`) bağlantı içinde artar; tekrar gelen ya da eski çerçeve
 *    atılır. Bakiye ayrıca `as_of` ile sıralanır (bkz. `newerWallet`).
 *  - Bağlantı sağlıklı değilken (`isLive() === false`) çağıranlar eski
 *    yoklamalarına (polling, long-poll) devam eder; kanal açılınca yoklama
 *    seyrekleşir. Kanal hiçbir işlemin kaynağı değildir.
 *  - Yeniden bağlanma üstel bekleme + rastgele sapmayla yapılır: sunucu
 *    yeniden başladığında bütün oyuncular aynı anda bağlanmaz.
 *  - 4401 (oturum geçersiz) ve 4403 (köken/izin) kapanışında yeniden
 *    denenmez; yeni jetonla `start` çağrılınca tekrar başlar.
 *  - Aynı oyuncunun birden çok sekmesi ayrı bağlantı açar (sunucu kullanıcı
 *    başına 6 ile sınırlar, en eskisini kapatır). Sekmeler arası tek bağlantı
 *    paylaşımı (BroadcastChannel liderliği) değerlendirildi; bağlantı başına
 *    maliyet düşük olduğu ve lider sekme kapanınca geçiş karmaşası getirdiği
 *    için uygulanmadı.
 */
import { API } from './api';

const BACKOFF_BASE_MS = 1000;
const BACKOFF_MAX_MS = 30000;
const FALLBACK_AFTER_FAILURES = 2;
const NO_RETRY = new Set([4401, 4403]);
// 4429: sunucu sınırı (kullanıcı başına bağlantı, IP, işçi). Kullanıcı sınırında
// sunucu en eski bağlantıyı kapatır; kapatılan sekme hemen yeniden bağlanırsa
// diğerini düşürür ve sekmeler birbirini sırayla kapatır (BETRUN 25.10.2026:
// tek iPhone 3 saatte 92 bağlantı, 77'si 10 sn arayla). Bu kapanıştan sonra
// en az LIMIT_RETRY_MS beklenir ve yalnız görünür sekme yeniden bağlanır.
const CLOSE_LIMIT = 4429;
export const LIMIT_RETRY_MS = 30000;

export const backoffDelay = (attempt, random = Math.random) => {
  const ceiling = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** Math.max(0, attempt));
  return Math.round(ceiling * (0.5 + random()));
};

/** Gelen bakiye elindekinden yeni mi? (`as_of` ms; eşitse sıra numarası belirler). */
export const newerWallet = (current, incoming) => {
  if (!incoming) return false;
  if (!current) return true;
  if (incoming.as_of !== current.as_of) return incoming.as_of > current.as_of;
  return (incoming._s || 0) > (current._s || 0);
};

export const socketUrl = (apiBase = API || '/api', location = typeof window !== 'undefined' ? window.location : null) => {
  if (typeof apiBase !== 'string' || !apiBase) apiBase = '/api'; // eslint-disable-line no-param-reassign
  if (/^https?:\/\//i.test(apiBase)) return `${apiBase.replace(/^http/i, 'ws').replace(/\/+$/, '')}/ws`;
  if (!location) return '';
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}${apiBase.replace(/\/+$/, '')}/ws`;
};

const createRealtime = ({ WebSocketImpl = typeof WebSocket !== 'undefined' ? WebSocket : null, url = socketUrl } = {}) => {
  const handlers = new Map();
  const statusHandlers = new Set();
  let ws = null;
  let token = '';
  let status = 'idle'; // idle | connecting | live | fallback | stopped
  let failures = 0;
  let lastSeq = 0;
  let retryTimer = null;
  let everLive = false;
  const stats = { connects: 0, reconnects: 0, events: 0, dropped: 0, lastClose: 0 };

  const setStatus = (next) => {
    if (status === next) return;
    status = next;
    statusHandlers.forEach((fn) => { try { fn(next); } catch { /* dinleyici hatası kanalı bozmaz */ } });
  };
  const emit = (type, data, frame) => {
    (handlers.get(type) || []).forEach((fn) => { try { fn(data, frame); } catch { /* yut */ } });
    (handlers.get('*') || []).forEach((fn) => { try { fn(data, frame); } catch { /* yut */ } });
  };
  const send = (message) => {
    if (ws && ws.readyState === 1) ws.send(JSON.stringify(message));
  };

  const schedule = () => {
    clearTimeout(retryTimer);
    const delay = backoffDelay(failures);
    retryTimer = setTimeout(connect, delay);
  };
  const whenVisible = (fn) => {
    if (typeof document === 'undefined' || document.visibilityState !== 'hidden') { fn(); return; }
    const onChange = () => {
      if (document.visibilityState === 'hidden') return;
      document.removeEventListener('visibilitychange', onChange);
      fn();
    };
    document.addEventListener('visibilitychange', onChange);
  };

  function connect() {
    clearTimeout(retryTimer);
    if (!token || !WebSocketImpl) { setStatus('fallback'); return; }
    let target = '';
    try { target = url(); } catch { target = ''; }
    if (!target) { setStatus('fallback'); return; }
    let socket;
    try {
      socket = new WebSocketImpl(target);
    } catch {
      failures += 1;
      setStatus('fallback');
      schedule();
      return;
    }
    ws = socket;
    lastSeq = 0;
    stats.connects += 1;
    if (everLive) stats.reconnects += 1;
    if (status !== 'live') setStatus(failures >= FALLBACK_AFTER_FAILURES ? 'fallback' : 'connecting');
    socket.onopen = () => { socket.send(JSON.stringify({ op: 'auth', token })); };
    socket.onmessage = (event) => {
      let frame;
      try { frame = JSON.parse(event.data); } catch { return; }
      if (!frame || typeof frame.t !== 'string') return;
      if (typeof frame.s === 'number') {
        if (frame.s <= lastSeq) { stats.dropped += 1; return; } // tekrar ya da eski çerçeve
        lastSeq = frame.s;
      }
      stats.events += 1;
      if (frame.t === 'ping') { send({ op: 'pong' }); return; }
      if (frame.t === 'hello') {
        failures = 0;
        const reconnect = everLive;
        everLive = true;
        setStatus('live');
        emit('hello', { ...frame.d, reconnect }, frame);
        return;
      }
      emit(frame.t, frame.d, frame);
    };
    socket.onclose = (event) => {
      if (ws !== socket) return;
      ws = null;
      stats.lastClose = event?.code || 0;
      if (status === 'stopped') return;
      if (NO_RETRY.has(event?.code)) { setStatus('fallback'); return; }
      failures += 1;
      setStatus(failures >= FALLBACK_AFTER_FAILURES ? 'fallback' : 'connecting');
      if (event?.code === CLOSE_LIMIT) {
        // Bu sekme bekler; yedek yol (bakiye beklemesi, yoklama) çalışmaya devam eder.
        setStatus('fallback');
        clearTimeout(retryTimer);
        retryTimer = setTimeout(() => {
          whenVisible(() => { if (token && !ws) connect(); });
        }, Math.max(LIMIT_RETRY_MS, backoffDelay(failures)));
        return;
      }
      schedule();
    };
    socket.onerror = () => { /* onclose ardından gelir */ };
  }

  return {
    /** Oturum açılınca (ya da jeton değişince) çağrılır. */
    start(nextToken) {
      if (nextToken === token && ws && status !== 'fallback') return;
      token = nextToken || '';
      failures = 0;
      if (ws) { const old = ws; ws = null; try { old.close(1000); } catch { /* yut */ } }
      if (!token) { setStatus('idle'); return; }
      connect();
    },
    stop() {
      clearTimeout(retryTimer);
      token = '';
      setStatus('stopped');
      everLive = false;
      if (ws) { const old = ws; ws = null; try { old.close(1000); } catch { /* yut */ } }
      setStatus('idle');
    },
    on(type, fn) {
      if (!handlers.has(type)) handlers.set(type, new Set());
      handlers.get(type).add(fn);
      return () => handlers.get(type)?.delete(fn);
    },
    onStatus(fn) { statusHandlers.add(fn); return () => statusHandlers.delete(fn); },
    isLive: () => status === 'live',
    status: () => status,
    send,
    watchPlayer: (id) => send({ op: 'watch_player', id }),
    unwatchPlayer: (id) => send({ op: 'unwatch_player', id }),
    stats: () => ({ ...stats, status, failures }),
  };
};

export { createRealtime };
const realtime = createRealtime();
if (typeof window !== 'undefined') window.__bhsRealtime = realtime; // tanılama (yalnız sayaçlar)
export default realtime;

/**
 * Kanal canlıyken yoklamayı seyrekleştiren yardımcı: `every` ms'de bir çalışan
 * bir zamanlayıcıda `shouldSkip()` true ise o tur atlanır (kanal canlı ve son
 * gerçek istek `liveEvery` ms'den yeniyse).
 */
export const pollGate = (liveEvery) => {
  let last = 0;
  return {
    skip(now = Date.now()) {
      if (!realtime.isLive()) { last = now; return false; }
      if (now - last < liveEvery) return true;
      last = now;
      return false;
    },
    touch(now = Date.now()) { last = now; },
  };
};
